// src/components/mensagens/MessageActionMenu.jsx
// ============================================================================
// 🎯 COMPONENTE: MessageActionMenu — V1 ENTERPRISE
// 📋 Função: Menu contextual para ações em mensagens (bolhas do histórico)
// 📱 Responsivo: Dropdown inteligente em desktop · Action Sheet nativo em mobile
// 🎨 Design: Glassmorphism · Animações GPU-accelerated · Auto-posicionamento
// 🔒 Robusto: Click outside · ESC para fechar · Foco automático · Anti-crash
// ============================================================================
// 🎮 Ações suportadas:
//    👁️ Ver detalhes   → Navega para MessageDetails
//    ✏️ Editar         → Modo edição inline
//    🔄 Reenviar       → Abre UniversalWhatsAppModal com texto pré-carregado
//    📋 Copiar texto   → Copia para clipboard com feedback
//    🗑️ Excluir        → Abre DeleteConfirmModal
// ============================================================================

import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Edit3,
  Forward,
  Copy,
  Check,
  Trash2,
  X,
  MoreHorizontal,
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { getMessageClipboardText } from './helpers';

// ============================================================================
// 🎨 CONFIGURAÇÃO DAS AÇÕES
// ============================================================================
const ACTION_CONFIG = {
  view: {
    id: 'view',
    label: 'Ver detalhes',
    mobileLabel: 'Ver detalhes da mensagem',
    icon: Eye,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    hover: 'hover:bg-blue-50',
    active: 'active:bg-blue-100',
    destructive: false,
  },
  edit: {
    id: 'edit',
    label: 'Editar',
    mobileLabel: 'Editar mensagem',
    icon: Edit3,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    hover: 'hover:bg-indigo-50',
    active: 'active:bg-indigo-100',
    destructive: false,
  },
  resend: {
    id: 'resend',
    label: 'Reenviar',
    mobileLabel: 'Reenviar mensagem',
    icon: Forward,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    hover: 'hover:bg-emerald-50',
    active: 'active:bg-emerald-100',
    destructive: false,
  },
  copy: {
    id: 'copy',
    label: 'Copiar texto',
    mobileLabel: 'Copiar texto da mensagem',
    icon: Copy,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    hover: 'hover:bg-purple-50',
    active: 'active:bg-purple-100',
    destructive: false,
  },
  delete: {
    id: 'delete',
    label: 'Excluir',
    mobileLabel: 'Excluir mensagem',
    icon: Trash2,
    color: 'text-red-600',
    bg: 'bg-red-50',
    hover: 'hover:bg-red-50',
    active: 'active:bg-red-100',
    destructive: true,
    separator: true,
  },
};

// ============================================================================
// 🎯 HOOK: useClickOutside
// ============================================================================
function useClickOutside(ref, handler, enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener, { passive: true });
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, enabled]);
}

// ============================================================================
// 🎯 HOOK: useAutoPosition
// ============================================================================
function useAutoPosition(triggerRef, menuRef, isOpen) {
  const [position, setPosition] = useState({ x: 'left', y: 'bottom' });

  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const raf = requestAnimationFrame(() => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const gap = 8;

      // Horizontal
      const spaceRight = viewportW - triggerRect.right;
      const spaceLeft = triggerRect.left;
      const x = menuRect.width > spaceRight && spaceLeft > spaceRight
        ? 'right'
        : 'left';

      // Vertical
      const spaceBottom = viewportH - triggerRect.bottom;
      const spaceTop = triggerRect.top;
      const y = menuRect.height > spaceBottom && spaceTop > spaceBottom
        ? 'top'
        : 'bottom';

      setPosition({ x, y });
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen, triggerRef, menuRef]);

  return position;
}

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================
function MessageActionMenu({
  isOpen = false,
  onClose,
  message = null,
  permissions = null,
  onView,
  onEdit,
  onResend,
  onCopy,
  onDelete,
  triggerRef = null,
}) {
  const isMobile = useIsMobile();
  const menuRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [closing, setClosing] = useState(false);
  const position = useAutoPosition(triggerRef, menuRef, isOpen);

  // ==========================================================================
  // 🔒 SEGURANÇA: Se não há mensagem ou permissões, não renderiza ações
  // ==========================================================================
  const perms = permissions || {
    canView: true,
    canEdit: false,
    canResend: false,
    canDelete: true,
    canCopy: !!message?.message,
  };

  // ==========================================================================
  // 🎮 HANDLERS COM TRATAMENTO ANTI-CRASH
  // ==========================================================================
  const safeCall = useCallback(
    (fn, ...args) => {
      try {
        if (typeof fn === 'function') {
          fn(...args);
        }
      } catch (error) {
        console.error('❌ [MessageActionMenu] Erro ao executar ação:', error);
      }
    },
    []
  );

  const handleAction = useCallback(
    (actionId) => {
      if (closing) return;

      switch (actionId) {
        case 'view':
          safeCall(onView, message);
          break;
        case 'edit':
          safeCall(onEdit, message);
          break;
        case 'resend':
          safeCall(onResend, message);
          break;
        case 'copy':
          handleCopy();
          break;
        case 'delete':
          safeCall(onDelete, message);
          break;
        default:
          break;
      }

      // Fechar menu após ação (exceto copy que tem feedback)
      if (actionId !== 'copy') {
        setClosing(true);
        setTimeout(() => {
          onClose?.();
          setClosing(false);
        }, 100);
      }
    },
    [message, onView, onEdit, onResend, onDelete, onClose, safeCall, closing]
  );

  const handleCopy = useCallback(async () => {
    if (copied) return;

    const text = getMessageClipboardText(message);
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      safeCall(onCopy, message);

      setTimeout(() => {
        setCopied(false);
        onClose?.();
      }, 1500);
    } catch (err) {
      console.error('❌ [MessageActionMenu] Erro ao copiar:', err);
    }
  }, [message, copied, onCopy, onClose, safeCall]);

  // ==========================================================================
  // 🎮 FECHAR COM ESC
  // ==========================================================================
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleEsc, { passive: true });
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // ==========================================================================
  // 🔒 CLICK OUTSIDE
  // ==========================================================================
  useClickOutside(menuRef, () => onClose?.(), isOpen);

  // ==========================================================================
  // 📱 BLOQUEAR SCROLL EM MOBILE
  // ==========================================================================
  useEffect(() => {
    if (isOpen && isMobile) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen, isMobile]);

  // ==========================================================================
  // 🏗️ CONSTRUIR LISTA DE AÇÕES BASEADO EM PERMISSÕES
  // ==========================================================================
  const actions = [
    ...(perms.canView ? [ACTION_CONFIG.view] : []),
    ...(perms.canEdit ? [ACTION_CONFIG.edit] : []),
    ...(perms.canResend ? [ACTION_CONFIG.resend] : []),
    ...(perms.canCopy ? [ACTION_CONFIG.copy] : []),
    ...(perms.canDelete ? [ACTION_CONFIG.delete] : []),
  ];

  // Se não há ações disponíveis, não renderiza nada
  if (actions.length === 0) return null;

  // ==========================================================================
  // ⏸️ EARLY RETURN
  // ==========================================================================
  if (!isOpen || !message) return null;

  // ==========================================================================
  // 🖼️ RENDER — MOBILE: Action Sheet Nativo
  // ==========================================================================
  if (isMobile) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[10002] flex items-end justify-center"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Ações da mensagem"
        >
          {/* Overlay escuro */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            style={{ willChange: 'opacity' }}
          />

          {/* Sheet */}
          <motion.div
            ref={menuRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              mass: 1,
            }}
            className={`
              relative w-full max-w-lg mx-auto
              bg-white rounded-t-3xl
              shadow-2xl
              overflow-hidden
            `}
            style={{
              contain: 'layout style paint',
              willChange: 'transform',
              boxShadow: '0 -20px 60px -10px rgba(0,0,0,0.3)',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle de arraste */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Ações</h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Fechar menu"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Lista de ações */}
            <div className="px-3 py-2 pb-4">
              {actions.map((action, index) => {
                const Icon = action.id === 'copy' && copied ? Check : action.icon;
                const isCopyAction = action.id === 'copy' && copied;

                return (
                  <React.Fragment key={action.id}>
                    {/* Separador antes de ação destrutiva */}
                    {action.separator && index > 0 && (
                      <div className="my-2 border-t border-gray-100" />
                    )}

                    <motion.button
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.04 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleAction(action.id)}
                      className={`
                        w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl
                        transition-all duration-100
                        ${action.hover} ${action.active}
                        ${action.destructive
                          ? 'active:bg-red-50'
                          : 'active:bg-gray-50'
                        }
                        group
                      `}
                    >
                      {/* Ícone */}
                      <div
                        className={`
                          p-2.5 rounded-xl transition-all
                          ${action.bg}
                          group-active:scale-95
                          ${isCopyAction ? 'bg-green-100' : ''}
                        `}
                      >
                        <Icon
                          size={20}
                          className={`
                            ${isCopyAction ? 'text-green-600' : action.color}
                            transition-transform group-active:scale-90
                          `}
                        />
                      </div>

                      {/* Label + Descrição */}
                      <div className="flex-1 text-left">
                        <p
                          className={`
                            text-sm font-semibold
                            ${action.destructive ? 'text-red-700' : 'text-gray-800'}
                            ${isCopyAction ? 'text-green-700' : ''}
                          `}
                        >
                          {isCopyAction ? 'Copiado!' : action.mobileLabel}
                        </p>
                      </div>

                      {/* Indicador de destrutivo */}
                      {action.destructive && (
                        <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                      )}
                    </motion.button>
                  </React.Fragment>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // ==========================================================================
  // 🖼️ RENDER — DESKTOP: Dropdown Inteligente
  // ==========================================================================
  const posClasses = {
    x: {
      left: 'left-0',
      right: 'right-0',
    },
    y: {
      bottom: 'top-full mt-2',
      top: 'bottom-full mb-2',
    },
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.92, y: position.y === 'top' ? 8 : -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: position.y === 'top' ? 8 : -8 }}
        transition={{
          duration: 0.15,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        className={`
          absolute z-[10002]
          ${posClasses.x[position.x]}
          ${posClasses.y[position.y]}
          min-w-[200px] max-w-[260px]
          bg-white/95 backdrop-blur-xl
          rounded-2xl
          shadow-2xl
          border border-white/20
          py-1.5
          overflow-hidden
        `}
        style={{
          contain: 'layout style paint',
          willChange: 'transform, opacity',
          boxShadow:
            '0 20px 40px -8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
        }}
        role="menu"
        aria-label="Ações da mensagem"
      >
        {actions.map((action, index) => {
          const Icon = action.id === 'copy' && copied ? Check : action.icon;
          const isCopyAction = action.id === 'copy' && copied;

          return (
            <React.Fragment key={action.id}>
              {/* Separador */}
              {action.separator && index > 0 && (
                <div className="my-1 border-t border-gray-100" />
              )}

              <motion.button
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleAction(action.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5
                  text-left transition-colors duration-75
                  ${action.hover}
                  ${action.destructive ? 'hover:bg-red-50' : ''}
                  group
                `}
                role="menuitem"
                style={{ contain: 'layout style' }}
              >
                {/* Ícone */}
                <div
                  className={`
                    p-1.5 rounded-lg transition-all
                    ${action.bg}
                    group-hover:scale-105
                    ${isCopyAction ? 'bg-green-100' : ''}
                  `}
                >
                  <Icon
                    size={16}
                    className={`
                      ${isCopyAction ? 'text-green-600' : action.color}
                      transition-transform group-hover:scale-110
                    `}
                  />
                </div>

                {/* Label */}
                <span
                  className={`
                    text-sm font-medium flex-1
                    ${action.destructive ? 'text-red-700' : 'text-gray-700'}
                    ${isCopyAction ? 'text-green-700' : ''}
                  `}
                >
                  {isCopyAction ? 'Copiado!' : action.label}
                </span>

                {/* Atalho de teclado (apenas visual) */}
                {action.id === 'copy' && !copied && (
                  <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded-md">
                    ⌘C
                  </kbd>
                )}
                {action.id === 'delete' && (
                  <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded-md">
                    ⌫
                  </kbd>
                )}
              </motion.button>
            </React.Fragment>
          );
        })}
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================================
// 🎯 MEMO + DISPLAY NAME
// ============================================================================
MessageActionMenu.displayName = 'MessageActionMenu';

// ============================================================================
// 🎯 EXPORTAÇÃO
// ============================================================================
export default memo(MessageActionMenu);